import { useEffect, useRef, useState } from 'react';
import type { Organization } from '@heybesafe/shared';
import { HbsButton } from '../../components/hbs/Button';
import { updateMemberName, clearMemberName, getOrg, getCurrentUserId } from '../../api/client';
import { encryptMemberName, decryptMemberName } from '../../crypto/memberName';
import { base64UrlToBytes } from '../../crypto/base64url';
import { useSafe } from '../../context/SafeContext';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import { getMemoizedMemberName } from '../../hooks/useMemberNames';
import { getCachedMemberNames, type CachedMemberName } from '../../storage/safe-store';

const MAX_NAME_LEN = 40;

interface Props {
  orgId: string;
  /** Called after a successful Save or Clear, in addition to the (expensive,
   *  debounced-by-nobody) `refreshOrgs()` call this component already makes.
   *  Hosts use this to cheaply refresh whatever roster is showing member
   *  names next to this card — see OverviewTab/MemberRosterCard and
   *  ManageTab, both of which sit directly below this card and would
   *  otherwise keep showing the stale name/email until an unrelated
   *  navigation or reload. */
  onSaved?: () => void;
}

/**
 * "How you appear in this family" — the member's own display-name control.
 *
 * Mounted on BOTH `OverviewTab` and `ManageTab`: the tab strip that would let
 * someone navigate between them is itself admin-only, `OrgDefaultRoute` sends
 * admins to `manage` and members to `overview`, and `ManageTab` redirects
 * non-admins away. A single mount would hide this from half the audience —
 * see the mount note in each host file. Do not collapse to one mount.
 *
 * The 1-40 character rule is enforced HERE and only here: the server receives
 * ciphertext and cannot check it.
 */
export function MyFamilyNameCard({ orgId, onSaved }: Props) {
  // Self-sufficient: fetch the org, decrypt only my own name for the initial
  // value. Neither host tab holds a member list or a names map to pass in.
  const { orgs, refreshOrgs } = useSafe();
  const keys = useFamilyKeys();
  const myUserId = getCurrentUserId() ?? '';
  // The family's public key is already in context — CategoriesTab reads it the
  // same way — so the initial paint needs no network at all.
  const orgFromContext = orgs.find((o) => o.id === orgId);
  const [orgPublicKey, setOrgPublicKey] = useState(orgFromContext?.publicKey ?? '');
  // Seed from the session memo the roster fills. On a re-visit this is already
  // populated, so the field renders with the name instead of blank-then-filled
  // — the behaviour that read as the page correcting itself.
  const memoized = getMemoizedMemberName(orgId, getCurrentUserId() ?? undefined);
  const [currentName, setCurrentName] = useState(memoized ?? '');
  const [value, setValue] = useState(memoized ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // Guards the mount/keys.version effect below: once the user has typed into
  // the field, a family key that hydrates late (or re-hydrates) must never
  // clobber their in-progress edit with the last-saved name.
  const touchedRef = useRef(false);

  // Shared by the mount effect and handleSave's recovery path — both need
  // "go fetch the org and hand back its publicKey", but only the mount
  // effect also wants to decrypt my own name out of the response.
  async function fetchOrgPublicKey(): Promise<string | null> {
    const result = await getOrg(orgId);
    if (!result.ok) return null;
    const org = result.data.org as Organization;
    setOrgPublicKey(org.publicKey);
    return org.publicKey;
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Cold memo but a warm cache (first visit of a new session): decrypt the
      // persisted ciphertext first so the field fills from local data in a few
      // milliseconds rather than after a round-trip. getOrg still runs below and
      // overwrites with the authoritative value.
      const pubNow = orgFromContext?.publicKey;
      if (!memoized && pubNow && !touchedRef.current) {
        const cached = getCachedMemberNames(orgId)?.find((m: CachedMemberName) => m.userId === myUserId);
        const privNow = keys.getKey(orgId);
        if (cached?.encryptedName && privNow) {
          try {
            const seeded = await decryptMemberName(cached, privNow, base64UrlToBytes(pubNow));
            if (!cancelled && !touchedRef.current) { setCurrentName(seeded); setValue(seeded); }
          } catch { /* stale or wrong-key cache — fall through to the network */ }
        }
      }

      const result = await getOrg(orgId);
      if (cancelled || !result.ok) return;
      const org = result.data.org as Organization;
      setOrgPublicKey(org.publicKey);
      const me = result.data.members.find((m) => m.userId === myUserId);
      if (!me || !me.encryptedName) return;
      const priv = keys.getKey(orgId);
      if (!priv) return; // key not hydrated yet — start empty, never block the editor
      try {
        const pub = base64UrlToBytes(org.publicKey);
        const name = await decryptMemberName(me, priv, pub);
        if (!cancelled) {
          setCurrentName(name);
          // Don't stomp on an in-progress edit — see touchedRef's comment.
          if (!touchedRef.current) setValue(name);
        }
      } catch {
        // Bad blob — start empty rather than blocking the editor.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, myUserId, keys.version]);

  async function handleSave() {
    const trimmed = value.trim();
    if (!trimmed) { setError('Enter a name, or clear it to go back to your email.'); return; }
    if (trimmed.length > MAX_NAME_LEN) { setError(`Keep it to ${MAX_NAME_LEN} characters or fewer.`); return; }
    setBusy(true); setError(null);
    try {
      // The initial getOrg on mount may have failed (network blip) and left
      // orgPublicKey empty — encrypting to an empty key would silently throw
      // inside the X25519 import and this Save could never succeed. Retry the
      // fetch here so a transient failure doesn't permanently brick the editor.
      const pubKey = orgPublicKey || (await fetchOrgPublicKey());
      if (!pubKey) {
        setError("Couldn't load your family's details. Check your connection and try again.");
        return;
      }
      const blob = await encryptMemberName(trimmed, base64UrlToBytes(pubKey));
      const res = await updateMemberName(orgId, myUserId, blob);
      if (!res.ok) { setError("Couldn't save that name. Please try again."); return; }
      setCurrentName(trimmed);
      setJustSaved(true);
      // Cheap, host-specific refresh (e.g. a single getOrg) so the roster
      // sitting right below this card reflects the new name immediately.
      // Separate from refreshOrgs below, which is expensive (listOrgs + a
      // getOrg per org) and does not bump FamilyKeysContext.version for an
      // already-cached key, so it alone never causes a re-render here.
      onSaved?.();
      void refreshOrgs();
    } catch {
      setError("Couldn't save that name. Please try again.");
    } finally { setBusy(false); }
  }

  async function handleClear() {
    setBusy(true); setError(null);
    try {
      const res = await clearMemberName(orgId, myUserId);
      if (!res.ok) { setError("Couldn't clear your name. Please try again."); return; }
      setValue('');
      setCurrentName('');
      setJustSaved(false);
      onSaved?.();
      void refreshOrgs();
    } catch {
      setError("Couldn't clear your name. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4">
      <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-2">
        How you appear in this family
      </h2>
      <p className="text-sm text-hbs-text-mute mb-3">
        Your family sees this name instead of your email address. Only you can change it,
        and it's encrypted — we can't read it.
      </p>
      <div className="flex flex-wrap items-start gap-2">
        <input
          id="family-display-name"
          aria-label="How you appear in this family"
          value={value}
          maxLength={MAX_NAME_LEN}
          onChange={(e) => {
            touchedRef.current = true;
            setJustSaved(false);
            setValue(e.target.value);
          }}
          placeholder="e.g. Sarah"
          className="flex-1 min-w-[12rem] bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text placeholder-hbs-text-mute focus:outline-none focus:border-hbs-primary"
        />
        <HbsButton id="save-family-name-button" onClick={() => void handleSave()} loading={busy}>
          Save
        </HbsButton>
        {currentName && (
          <HbsButton id="clear-family-name-button" variant="ghost" onClick={() => void handleClear()} disabled={busy}>
            Clear
          </HbsButton>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-hbs-danger">{error}</p>}
      {!error && justSaved && (
        <p className="mt-2 text-sm text-hbs-primary" role="status">Saved.</p>
      )}
    </section>
  );
}
